/**
 * LargeBondDetailModal - Read-only detail viewer for a large bond
 */

import {
  OathModal,
  OathButton,
  OathDetailSection,
  OathSectionCard,
} from '../../shared/OathUI';
import {
  UserIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  UsersIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { STATUS_LABELS, STATUS_COLORS, COLLATERAL_TYPES } from './shared/largeBondConstants';
import { formatCurrency, formatDate, formatAddress } from '../transferbonds/shared/formatUtils';

const LargeBondDetailModal = ({ isOpen, onClose, bond }) => {
  if (!bond) return null;

  const statusLabel = STATUS_LABELS[bond.status] || bond.status;
  const statusColor = STATUS_COLORS[bond.status] || 'bg-gray-100 text-gray-800';

  const collateralLabel = (type) =>
    COLLATERAL_TYPES.find((ct) => ct.value === type)?.label || type || '-';

  return (
    <OathModal
      isOpen={isOpen}
      onClose={onClose}
      title="Large Bond Details"
      size="xl"
      footer={
        <OathButton variant="outline" onClick={onClose} className="ml-auto">
          Close
        </OathButton>
      }
    >
      <div className="space-y-5">
        {/* Bond Information */}
        <OathSectionCard title="Bond Information" icon={CurrencyDollarIcon}>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1">
            <div className="py-2">
              <dt className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Status</dt>
              <dd className="mt-1">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColor}`}>
                  {statusLabel}
                </span>
              </dd>
            </div>
            <OathDetailItem label="Amount" value={formatCurrency(bond.amount)} />
            <OathDetailItem label="Requested By" value={bond.requestedBy} />
            <OathDetailItem label="Ref #" value={bond.refNumber} />
            <OathDetailItem label="Callback Phone" value={bond.callbackPhone} />
            <OathDetailItem label="Date Created" value={formatDate(bond.dateCreated)} />
          </div>
        </OathSectionCard>

        {/* Defendant Information */}
        <OathSectionCard title="Defendant Information" icon={UserIcon}>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1">
            <OathDetailItem
              label="Name"
              value={[bond.firstName, bond.middleInitial, bond.lastName].filter(Boolean).join(' ')}
            />
            <OathDetailItem label="Date of Birth" value={formatDate(bond.dateOfBirth)} />
            <OathDetailItem label="Age" value={bond.age} />
            <OathDetailItem label="SSN" value={bond.ssn} />
            <OathDetailItem label="Occupation" value={bond.occupation} />
            <OathDetailItem label="Income" value={bond.income != null ? formatCurrency(bond.income) : '-'} />
            <div className="col-span-2 sm:col-span-3">
              <OathDetailItem
                label="Address"
                value={formatAddress(bond.street1, bond.street2, bond.city, bond.state, bond.zip)}
              />
            </div>
          </div>

          {/* Citizenship info */}
          {bond.isUsCitizen === false && (
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">Citizenship</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1">
                <OathDetailItem label="US Citizen" value="No" />
                <OathDetailItem label="Country of Origin" value={bond.countryOfOrigin} />
                <OathDetailItem label="Immigration Status" value={bond.immigrationStatus} />
                {bond.citizenDescription && (
                  <div className="col-span-2 sm:col-span-3">
                    <OathDetailItem label="Description" value={bond.citizenDescription || bond.description} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Transfer bond */}
          {bond.isTransferBond && (
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                <OathDetailItem label="Transfer Bond" value="Yes" />
                <OathDetailItem label="Origin" value={bond.tbondOriginDescription} />
              </div>
            </div>
          )}
        </OathSectionCard>

        {/* Bond Conditions */}
        {(bond.courtOrderedMonitor || bond.agentRequiredMonitor || bond.pretrialIntervention || bond.drugTesting || bond.checkInType || bond.conditionDescription) && (
          <OathSectionCard title="Bond Conditions" icon={ShieldCheckIcon}>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1">
              {bond.courtOrderedMonitor && <OathDetailItem label="Court Ordered Monitor" value="Yes" />}
              {bond.agentRequiredMonitor && <OathDetailItem label="Agent Required Monitor" value="Yes" />}
              {bond.pretrialIntervention && <OathDetailItem label="Pretrial Intervention" value="Yes" />}
              {bond.drugTesting && <OathDetailItem label="Drug Testing" value="Yes" />}
              <OathDetailItem label="Check-in Type" value={bond.checkInType} />
              <OathDetailItem label="Check-in Frequency" value={bond.checkInFrequency} />
            </div>
            {bond.conditionDescription && (
              <div className="mt-2">
                <OathDetailItem label="Description" value={bond.conditionDescription} />
              </div>
            )}
          </OathSectionCard>
        )}

        {/* Offense */}
        {bond.offense && (
          <OathSectionCard title="Charges" icon={DocumentTextIcon}>
            <OathDetailItem label="Offense / Charge" value={bond.offense} />
          </OathSectionCard>
        )}

        {/* Indemnitors */}
        {Array.isArray(bond.indemnitors) && bond.indemnitors.length > 0 && (
          <OathSectionCard title="Indemnitor(s)" icon={UsersIcon}>
            <div className="space-y-4">
              {bond.indemnitors.map((ind, i) => (
                <div key={i} className="border-l-4 border-gray-200 dark:border-gray-600 pl-4">
                  <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                    Indemnitor #{i + 1}
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1">
                    <OathDetailItem
                      label="Name"
                      value={[ind.firstName, ind.lastName].filter(Boolean).join(' ')}
                    />
                    <OathDetailItem label="Relationship" value={ind.relationshipToDefendant} />
                    <OathDetailItem label="Occupation" value={ind.occupation} />
                    {ind.durationOfEmployment && (
                      <OathDetailItem label="Duration of Employment" value={ind.durationOfEmployment} />
                    )}
                  </div>

                  {/* Collaterals */}
                  {ind.collaterals?.map((coll, j) => (
                    <div key={j} className="mt-2 ml-4 pl-3 border-l-2 border-gray-100 dark:border-gray-700">
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                        Collateral #{j + 1}
                      </p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1">
                        <OathDetailItem label="Type" value={collateralLabel(coll.collateralType)} />
                        {coll.collateralAmount && (
                          <OathDetailItem label="Amount" value={formatCurrency(Number(coll.collateralAmount))} />
                        )}
                        {coll.liens && <OathDetailItem label="Liens" value={coll.liens} />}
                        {coll.collateralType === 'DE' && (
                          <div className="col-span-2 sm:col-span-3">
                            <OathDetailItem
                              label="Address"
                              value={formatAddress(coll.collateralStreet1, coll.collateralStreet2, coll.collateralCity, coll.collateralState, coll.collateralZip)}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </OathSectionCard>
        )}

        {/* Approved Bond Details */}
        {bond.status === 'AP' && (
          <OathSectionCard title="Approved Bond Details" icon={ShieldCheckIcon}>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1">
              <OathDetailItem label="Authorization #" value={bond.authorizationNumber} />
              <OathDetailItem label="Offense" value={bond.offense} />
            </div>

            {/* Powers list */}
            {Array.isArray(bond.powers) && bond.powers.length > 0 ? (
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 space-y-3">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Powers ({bond.powers.length})</p>
                {bond.powers.map((power, i) => (
                  <div key={power.largeBondCourtInfoId || i} className="border-l-4 border-gray-200 dark:border-gray-600 pl-4">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Power #{i + 1}</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1">
                      <OathDetailItem label="Power Number" value={power.powerNumber} />
                      <OathDetailItem label="Execution Amount" value={power.executionAmount != null ? formatCurrency(power.executionAmount) : '-'} />
                    </div>
                  </div>
                ))}
              </div>
            ) : bond.powerNumber ? (
              /* Backward compat: show scalar fields if no powers array */
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1">
                  <OathDetailItem label="Power Number" value={bond.powerNumber} />
                  <OathDetailItem label="Execution Amount" value={bond.executionAmount != null ? formatCurrency(bond.executionAmount) : '-'} />
                </div>
              </div>
            ) : null}
          </OathSectionCard>
        )}

        {/* Additional Details */}
        {bond.additionalDetails && (
          <OathSectionCard title="Additional Details" icon={DocumentTextIcon}>
            <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
              {bond.additionalDetails}
            </p>
          </OathSectionCard>
        )}
      </div>
    </OathModal>
  );
};

// Inline detail item helper (avoids importing OathDetailField for simpler layout)
const OathDetailItem = ({ label, value }) => (
  <div className="py-1.5">
    <dt className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</dt>
    <dd className="mt-0.5 text-sm font-medium text-gray-900 dark:text-white">{value || '-'}</dd>
  </div>
);

export default LargeBondDetailModal;
